import moment from 'moment';

import { type BadgeProps } from '@common/ui/Badge';

import {
  type BillingSubscriptionStatus,
  type CurrentBillingSubscription,
  type PaymentHistoryStatus,
  type SubscriptionPaymentMethod,
} from '@services/PaymentsService';

export interface StatusView {
  text: string;
  color: BadgeProps['color'];
}

const subscriptionStatusViews: Record<BillingSubscriptionStatus, StatusView> = {
  pending: { text: 'Ожидает оплаты', color: 'actionSecondary' },
  active: { text: 'Активна', color: 'positiveSecondary' },
  cancelled: { text: 'Отменена', color: 'warningSecondary' },
  expired: { text: 'Истекла', color: 'neutralSecondary' },
  stopped: { text: 'Остановлена', color: 'neutralSecondary' },
};

const paymentStatusViews: Record<PaymentHistoryStatus, StatusView> = {
  processing: { text: 'В обработке', color: 'neutralSecondary' },
  succeeded: { text: 'Оплачено', color: 'positiveSecondary' },
  failed: { text: 'Ошибка', color: 'negativeSecondary' },
  cancelled: { text: 'Отменён', color: 'neutralSecondary' },
  refunded: { text: 'Возврат', color: 'warningSecondary' },
};

const fallbackStatusView = (status: string): StatusView => ({
  text: status,
  color: 'neutralSecondary',
});

export const getSubscriptionStatusView = (
  status: BillingSubscriptionStatus,
): StatusView => subscriptionStatusViews[status] || fallbackStatusView(status);

export const getPaymentStatusView = (
  status: PaymentHistoryStatus,
): StatusView => paymentStatusViews[status] || fallbackStatusView(status);

export const formatDate = (value?: string | null): string | null =>
  value ? moment(value).format('DD.MM.YYYY') : null;

export const formatPlanName = (planName?: string | null): string => {
  switch (planName) {
    case 'Monthly':
      return 'Premium на 1 месяц';
    case 'Quarterly':
      return 'Premium на 3 месяца';
    case 'Annual':
      return 'Premium на 12 месяцев';
    default:
      return planName ? `Premium — ${planName}` : 'Premium';
  }
};

export const formatProductCode = (productCode?: string | null): string => {
  switch (productCode) {
    case 'premium.monthly':
      return 'Premium на 1 месяц';
    case 'premium.quarterly':
      return 'Premium на 3 месяца';
    case 'premium.annual':
      return 'Premium на 12 месяцев';
    default:
      return 'Платеж';
  }
};

export const formatAmount = (
  amount: number,
  currency?: string | null,
): string => {
  switch (currency) {
    case 'XTR':
      return `${amount} ⭐`;
    case 'RUB':
    case null:
    case undefined:
      return `${amount} ₽`;
    default:
      return `${amount} ${currency}`;
  }
};

export const formatPaymentMethod = (
  paymentMethod?: SubscriptionPaymentMethod | null,
): string | null => {
  if (!paymentMethod) {
    return null;
  }

  if (paymentMethod.card?.last4) {
    const cardType = paymentMethod.card.cardType
      ? `${paymentMethod.card.cardType.toUpperCase()} `
      : '';

    return `${cardType}**** ${paymentMethod.card.last4}`;
  }

  if (paymentMethod.title) {
    return paymentMethod.title;
  }

  // Raw YooKassa method types are API identifiers, not display names — map
  // the saved-method ones the paywall sells to their brand labels.
  if (paymentMethod.type === 'sbp') {
    return 'СБП';
  }
  if (paymentMethod.type === 'sberbank') {
    return 'SberPay';
  }

  return paymentMethod.type;
};

export const isBillingSubscriptionVisible = (
  subscription?: CurrentBillingSubscription | null,
): subscription is CurrentBillingSubscription => {
  if (!subscription) {
    return false;
  }

  if (subscription.status === 'active' || subscription.status === 'pending') {
    return true;
  }

  return (
    subscription.status === 'cancelled' &&
    Boolean(subscription.expiresAt) &&
    moment(subscription.expiresAt).isAfter(moment())
  );
};

/**
 * Mirrors the server's `assertNoActiveAutoRenewingSubscription` guard: a live
 * auto-renewing subscription anywhere makes a second recurring checkout a
 * guaranteed 409. Gating the buy button on this keeps the paywall honest
 * instead of letting the user tap into an error they cannot resolve.
 *
 * Deliberately narrower than `isBillingSubscriptionVisible`: that one also
 * covers non-auto-renewing rows, which the server does NOT reject, so reusing
 * it here would block legitimate one-off purchases.
 */
export const blocksNewRecurringPurchase = (
  subscription?: CurrentBillingSubscription | null,
): boolean => {
  if (!subscription || !subscription.autoRenewing) {
    return false;
  }

  if (!['pending', 'active', 'cancelled'].includes(subscription.status)) {
    return false;
  }

  return (
    !subscription.expiresAt || moment(subscription.expiresAt).isAfter(moment())
  );
};

export const canCancelSubscription = (
  subscription: CurrentBillingSubscription,
): boolean =>
  subscription.autoRenewing &&
  (subscription.status === 'active' || subscription.status === 'pending');
